import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { IonSearchbar, ModalController } from '@ionic/angular';
import {
  Observable,
  Subject,
  debounceTime,
  distinctUntilChanged,
  from,
  map,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { AutoCompleteDataSourceEnum } from 'src/app/shared/components/modals/auto-complete-modal/enums/auto-complete-data-source.enum';
import { AutoCompleteService } from './services/auto-complete.service';

// https://medium.com/@alejandrolp1986/google-maps-places-y-autocomplete-en-tu-ionic-5-app-c34e314b2f5a
@Component({
  selector: 'app-auto-complete-modal',
  templateUrl: './auto-complete-modal.component.html',
  styleUrls: ['./auto-complete-modal.component.scss'],
})
export class AutoCompleteModalComponent implements OnInit, OnChanges {
  @ViewChild('searchbar', { static: false }) searchbar: IonSearchbar;

  @Output() suburbSelected = new EventEmitter<any>();

  // Service method responsible for fetching data. Must accept string param.
  @Input() dataSource: AutoCompleteDataSourceEnum;

  /** Optional - Property that needs to be set as control value. */
  @Input() valueField = '';

  /** Field key of returned objects that is displayed to the user. */
  @Input() displayField = '';

  @Input() placeholder = 'Search suburb, postcode';

  @Input() fundingSourceCode = 'home_care_package';

  @Input() searchString: string;

  @Input() projectedContent: TemplateRef<any>;

  @Input() emptyScreenOnNoRecordsOnly = false;

  // Title to display in appbar
  @Input() title = '';

  // Emitting search results from API
  searchResults$: Observable<any[]>;

  // Emitting search string
  searchString$: Subject<string> = new Subject();

  // Storing accessible text about search results
  searchResultsAriaLive: string;

  constructor(
    public modalController: ModalController,
    public autoCompleteService: AutoCompleteService,
  ) {}

  ngOnInit() {
    // Initialising search results observable
    this.searchResults$ = this.searchString$.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      startWith(this.searchString || ''),
      switchMap((searchString) => {
        if (searchString.length > 0) {
          return from(
            this.autoCompleteService.search(this.dataSource, {
              searchString,
              fundingSourceCode: this.fundingSourceCode,
            }),
          );
        }
        return of([]);
      }),
      // providing accessibility hints
      tap((results) => {
        const accessibleText = `Showing ${results.length} records for your search.`;
        this.searchResultsAriaLive = results.length
          ? `${accessibleText} Use swipe gestures to explore options.`
          : accessibleText;
      }),
      // return max 20 results
      map((results) => (results?.length > 20 ? results.slice(0, 20) : results)),
    );

    // Log error if dataSource method isn't provided
    if (!this.dataSource) {
      console.error("'dataSource' is required");
    }
    // log error if the form value is set to be the original API object but the displayField is not provided.
    if (!this.displayField) {
      console.error("'displayField' is required");
    }
    // setFocus ionic 5 workaround
    // https://github.com/ionic-team/ionic-framework/issues/17745
    setTimeout(() => this.searchbar.setFocus(), 500);
  }

  ionViewDidEnter() {
    this.searchbar.setFocus();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.focus?.currentValue) {
      this.searchbar.setFocus();
    }
  }

  updateSearchResults(searchString: string) {
    this.searchString$.next(searchString);
  }

  /**
   * Typically called onclick of searchResults option
   * emits the selected value and changes state to reflect the selection.
   *
   * @param option - The selected option object.
   */
  selectSearchResult(option: any) {
    if (this.valueField) {
      this.modalController.dismiss(option[this.valueField]);
    } else {
      this.modalController.dismiss(option);
    }
  }

  dismiss() {
    this.modalController.dismiss();
  }

  clearAutocomplete() {
    this.searchString$.next('');
  }
}
